import React from 'react';
import { Pagination, Button, Modal, Spin } from 'antd';
import { ProPageHeader as PageHeader } from '@ant-design/pro-layout';
import { DeleteFilled } from '@ant-design/icons';
import SortableTableHeader from '../../../components/common/SortableTableHeader';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import {
  getComp,
  resetCompUserGamePoints,
  resetAllCompUserGamePoints,
  removeComp,
} from '../../../store/slices/adminSlice';
import { useTableState, useDeleteConfirm, useDataLoading } from '../../../hooks';

const { confirm } = Modal;

// TypeScript interfaces
interface SportData {
  _id: string;
  sportname: string;
}

interface CompData {
  _id: string;
  competitionname: string;
  sport: SportData;
  totalCount: number;
}

const Comp: React.FC = () => {
  const dispatch = useAppDispatch();

  // Redux state
  const compData = useAppSelector((state) => state.admin.compData) as CompData[];
  const totalCount = useAppSelector((state) => state.admin.compCount);

  // Use custom hooks for common patterns
  const { currentPage, sortOrder, changePage, sortData, setCurrentPage } = useTableState({
    fetchAction: getComp,
  });

  const { showDeleteConfirm } = useDeleteConfirm({
    deleteAction: removeComp,
    idParamName: 'compId',
    onBeforeDelete: () => setCurrentPage(0),
  });

  const [loading] = useDataLoading(compData);

  const showConfirmResetPoints = (compId: string, sportId: string) => {
    const obj = {
      params: {
        compId: compId,
        sportId: sportId,
      },
    };
    confirm({
      title: 'Do you want to reset game points?',
      content: '',
      onOk() {
        dispatch(resetCompUserGamePoints(obj));
      },
      onCancel() {},
    });
  };

  const showConfirmResetPointsAll = () => {
    const obj = { params: {} };
    confirm({
      title: 'Do you want to reset all competitions game points?',
      content: '',
      onOk() {
        dispatch(resetAllCompUserGamePoints(obj));
      },
      onCancel() {},
    });
  };

  // Render comp rows
  const compRows = compData.map((x) => (
    <tr key={x._id}>
      <td>{x.competitionname}</td>
      <td>{x.sport.sportname}</td>
      <td>{x.totalCount}</td>
      <td className="action-icons">
        <Button
          type="link"
          className="btn-sm mr-2"
          onClick={() => showConfirmResetPoints(x._id, x.sport._id)}
        >
          <i className="fa fa-anchor" style={{ color: '#666' }}></i>
        </Button>
        <Button
          type="link"
          className="btn-sm mr-2"
          onClick={() => showDeleteConfirm(x._id)}
        >
          <DeleteFilled style={{ color: '#666' }} />
        </Button>
      </td>
    </tr>
  ));

  return (
    <>
      <div className="box box-default mb-4">
        <PageHeader
          prefixedClassName="ant-page-header"
          title="Competition"
          subTitle=""
          extra={[
            <Button
              key="1"
              type="default"
              onClick={showConfirmResetPointsAll}
            >
              Reset All
            </Button>,
          ]}
        />
        <Spin spinning={loading} delay={500}>
          <div className="box-body">
            <div className="table-responsive">
              <table className="table">
                <thead>
                  <tr>
                    <SortableTableHeader
                      label="Competition Name"
                      sortKey="competitionname"
                      currentSortOrder={sortOrder}
                      onSort={sortData}
                    />
                    <th scope="col">Sport</th>
                    <th scope="col">Total Joined User</th>
                    <th scope="col">Action</th>
                  </tr>
                </thead>
                <tbody>{compRows}</tbody>
              </table>
            </div>
            <Pagination
              style={{ textAlign: 'right' }}
              pageSize={10}
              defaultCurrent={currentPage}
              total={totalCount ? totalCount : 1}
              onChange={changePage}
            />
          </div>
        </Spin>
      </div>
    </>
  );
};

export default Comp;
